<template>
  <div
    class="mt-1 motor-dashboard-prime-scope common-table-root"
    :class="{ 'common-table-root--fill-viewport': fillViewportHeight }"
    :style="fillViewportRootStyle"
  >
    <div class="flex justify-between items-center mb-4" v-if="isExportCSV">
      <div class="flex justify-between items-center space-x-5 filter-container">
        <Button type="button" icon="pi pi-filter-slash" outlined @click="handleReset" size="small"
          v-tooltip.top="'Reset Filters'" v-if="filtersLocal" />
        <Button type="button" label="Export To CSV" @click="exportCSV" size="small" />
      </div>
    </div>
    <div class="common-table-datatable-shell">
    <DataTable ref="dataTable" removableSort showGridlines :value="tableData" :selection="selectedRecord"
      @update:selection="$emit('update:selectedRecord', $event)" :loading="loading" :dataKey="dataKey"
      :scrollable="fillViewportHeight"
      :scrollHeight="fillViewportHeight ? 'flex' : undefined"
      :tableStyle="fluidTableStyle" :paginator="paginationEnabled" :rows="rows" :totalRecords="totalRecords"
      :first="first" :rowsPerPageOptions="[5, 10, 20, 50]" @page="onPage" :lazy="true" @sort="onSort"
      :sortField="sortField" :sortOrder="sortOrder" v-model:expandedRows="expandedRows" @row-expand="onRowExpand"
      @row-collapse="onRowCollapse" :rowExpansionMode="'single'" filterDisplay="menu"
      :globalFilterFields="['journey_id']" v-model:filters="filtersLocal" @filter="onFilter"
      :rowSelectable="rowSelectable" class="custom-datatable"
      paginatorTemplate="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink RowsPerPageDropdown"
      currentPageReportTemplate="{first} to {last} of {totalRecords}">
      <template #loading>
        <div v-if="rowLoading" class="loading-container">
          <i class="pi pi-spin pi-spinner" style="font-size: 3rem"></i>
          <span>{{ loadingMessage }}</span>
        </div>
      </template>

      <Column selectionMode="multiple" headerStyle="width: 3rem" v-if="isSelectionEnabled" />
      <Column expander headerStyle="width: 3rem" v-if="isExpanderEnabled" />

      <Column v-for="(col, index) in columns" :key="col.field + '_' + index" :field="col.field" :header="col.header" :sortable="true"
        :filter="columnHasFilter(col)" :showFilterMenu="true" :showFilterMatchModes="false" :style="col.style"
        :showFilterMenuOptions="false" :showFilterOperator="false" :showClearButton="false" :showApplyButton="false"
        filterMenuClass="common-table-column-filter-menu">
        <template #body="slotProps">
          <Tag
            v-if="['value'].includes(col.field)"
            :class="{
              'cursor-pointer': isValueColumnInteractive(slotProps.data),
              'cursor-not-allowed': !isValueColumnInteractive(slotProps.data),
            }"
            :severity="valueColumnSeverity(slotProps.data)"
            @click="onValueColumnClick(slotProps.data)"
          >
            {{ valueColumnLabel(slotProps.data) }}
          </Tag>
          <Tag v-else-if="['status'].includes(col.field)" class="pointer"
            :severity="getStatusColor(slotProps.data[col.field])">
            {{ slotProps.data[col.field] || '--' }}
          </Tag>
          <span v-else-if="col.isHtml" v-html="stripHtml(col.bodyTemplate(slotProps.data))"></span>
          <span
            v-else-if="['created_at', 'transaction_date', 'journey_created_date', 'proposal_updated_at', 'startDate', 'endDate', 'createdDate', 'modifiedDate'].includes(col.field)">
            {{ formatDate(slotProps.data[col.field]) || '--' }}
          </span>
          <span v-else-if="col.field === 'total_premium'">
            {{ formatPremiumINR(slotProps.data[col.field]) }}
          </span>
          <span
            v-else-if="['journey_id'].includes(col.field)"
            class="d-inline-flex align-items-center gap-1 journey-id-cell"
          >
            <span class="journey-id-cell__text text-break">{{ slotProps.data[col.field] || '--' }}</span>
            <Button
              v-if="slotProps.data[col.field]"
              type="button"
              icon="pi pi-copy"
              text
              rounded
              size="small"
              class="journey-id-copy-btn"
              v-tooltip.top="'Copy Journey Id'"
              @click.stop="copyJourneyId(slotProps.data[col.field])"
            />
          </span>
          <Tag v-else-if="col.field === 'journeyMode'"
            :value="slotProps.data[col.field] === 'assigned' ? 'Assigned to RM' : `Created by ${slotProps.data.role}`"
            :severity="slotProps.data[col.field] === 'assigned' ? 'info' : 'secondary'" icon="pi pi-user" />
          <Tag v-else-if="col.field === 'approvar_id' && currentRole.isAv"
            :value="slotProps.data[col.field] ? ' Assigned' : ` Un-Assigned`"
            :severity="slotProps.data[col.field] ? 'success' : 'secondary'"
            :icon="slotProps.data[col.field] ? 'pi pi-check' : 'pi pi-info-circle'" />
          <Tag v-else-if="col.field === 'active' || col.field === 'isActive'"
            :value="slotProps.data[col.field] === 1 || slotProps.data[col.field] === true ? 'Active' : 'Inactive'"
            :severity="(slotProps.data[col.field] === 1 || slotProps.data[col.field] === true) ? 'success' : 'secondary'" />
          <span v-else>
            {{ slotProps.data[col.field] || '--' }}
          </span>
        </template>
        <template #filtericon="slotProps">
          <i
            v-if="columnHasFilter(col)"
            :class="[
              slotProps?.class,
              isColumnFilterActive(col.field) ? 'pi pi-filter-fill' : 'pi pi-filter',
            ]"
            :style="isColumnFilterActive(col.field) ? 'color: var(--p-primary-color, #3b39d9)' : ''"
          />
        </template>
        <template #filter="{ filterModel, filterCallback }">
          <div v-if="columnHasFilter(col)" class="column-filter-inner">
            <MultiSelect
              v-if="isStatusDropdownColumnFilter(col)"
              :modelValue="statusFilterModelValue(filterModel)"
              :options="statusFilterSelectOptions"
              optionLabel="Text"
              optionValue="Value"
              placeholder="Select status (multiple)"
              display="chip"
              :maxSelectedLabels="1"
              selectedItemsLabel="+{0}"
              filter
              panelClass="common-table-status-multiselect-panel"
              class="column-filter-multiselect"
              :showClear="true"
              @update:modelValue="(val) => onStatusMultiFilterChange(val, filterModel, filterCallback)"
            />
            <Select
              v-else-if="isInsurerDropdownColumnFilter(col)"
              :model-value="filterModel.value"
              :options="insurerFilterSelectOptions"
              option-label="Text"
              option-value="Value"
              placeholder="Select insurer"
              :filter="insurerFilterSelectOptions.length > 7"
              filter-placeholder="Search..."
              :show-clear="true"
              class="column-filter-multiselect"
              @update:model-value="(val) => onInsurerFilterChange(val, filterModel, filterCallback)"
            />
            <InputText
              v-else
              v-model="filterModel.value"
              type="text"
              :placeholder="`Search by ${col.header}`"
              class="column-filter-input"
            />

            <div
              class="column-filter-actions d-flex align-items-center mt-3"
              :class="isColumnFilterApplyOnChange(col) ? 'justify-content-start' : 'justify-content-between'"
            >
              <Button label="Clear" severity="secondary"
                @click="() => onFilterColumnClear(col, filterModel, filterCallback)" />
              <Button
                v-if="!isColumnFilterApplyOnChange(col)"
                label="Apply"
                severity="success"
                @click="() => filterCallback()"
              />
            </div>
          </div>
        </template>
      </Column>
      <Column v-if="showToggleAction" field="toggleAction" header="Active" :style="{ minWidth: '5.5rem' }" :sortable="false">
        <template #body="slotProps">
          <InputSwitch :modelValue="isRowActive(slotProps.data)" 
            @update:modelValue="handleToggleChange(slotProps.data)" />
        </template>
      </Column>
      <Column v-if="showDeleteAction" field="deleteAction" header="Actions" :style="{ minWidth: '5.5rem' }" :sortable="false">
        <template #body="slotProps">
          <Button type="button" icon="pi pi-trash" severity="danger" text rounded 
            @click="handleDeleteClick(slotProps.data)" 
            v-tooltip.top="'Delete'" />
        </template>
      </Column>
      <Column field="" header="">
        <template #body="data">
          <div class="button-data-table" v-if="isMenuVisible(data)">
            <Button type="button" icon="pi pi-ellipsis-v" @click="(event) => showMenu(event, data)" aria-haspopup="true"
              aria-controls="overlay_menu" />
            <Menu ref="menu" id="overlay_menu" :model="items" :popup="true" />
          </div>
        </template>
      </Column>
      <template #expansion="slotProps">
        <CommonTableComponent :dataKey="'id'" :columns="innerColumns" :tableData="subTableDataMap[slotProps.data.id]"
          :rowLoading="innerTableQuery.rowLoading" :paginationEnabled="true"
          :totalRecords="subTableDataMap[slotProps.data.id]?.length || 0" :rows="5" :first="subFirst"
          :dataFetched="innerTableQuery.dataFetched" :isSelectionEnabled="false" :isExpanderEnabled="false"
          @page-change="innerPageChange" file-name="Payment History" :isExportCSV="false" :get-actions="() => []" />
      </template>
    </DataTable>
    </div>
    <div class="flex justify-center items-center no_data_fetch">
      <p v-if="dataFetched && tableData.length === 0" class="text-center">
        {{ noData }}
      </p>
      <p v-else-if="!dataFetched" class="flex justify-center items-center flex-col text-center space-y-3">
        <span>{{ loadingMessage }} </span>
      </p>
    </div>
  </div>
</template>

<script>
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Button from 'primevue/button';
import Menu from 'primevue/menu';
import Tag from 'primevue/tag';
import InputSwitch from 'primevue/inputswitch';
import { FilterMatchMode } from '@primevue/core/api';
import { formatToINR } from '@/mixins/utils';
import { stripHtml } from '@/utils/inputSanitizer';

export default {
  name: 'CommonTableComponent',
  components: {
    DataTable,
    Column,
    InputText,
    MultiSelect,
    Select,
    Button,
    Menu,
    Tag,
    InputSwitch
  },
  props: {
    tableData: {
      type: Array,
      default: () => []
    },
    selectedRecord: {
      type: Array,
      default: () => []
    },
    columns: {
      type: Array,
      required: true
    },
    rowLoading: {
      type: Boolean,
      default: false
    },
    paginationEnabled: {
      type: Boolean,
      default: true
    },
    totalRecords: {
      type: Number,
      default: 0
    },
    rows: {
      type: Number,
      default: 10
    },
    first: {
      type: Number,
      default: 0
    },
    dataFetched: {
      type: Boolean,
      default: false,
    },
    isSelectionEnabled: {
      type: Boolean,
      default: false
    },
    fileName: {
      type: String,
    },
    isExpanderEnabled: {
      type: Boolean,
      default: false
    },
    dataKey: {
      type: String,
      default: "journey_id"
    },
    filters: {
      type: Object,
    },
    isExportCSV: {
      type: Boolean,
      default: true
    },
    innerColumns: {
      type: Array,
      default: () => []
    },
    subInnerTableDataMap: {
      type: Array,
      default: () => ([])
    },
    subTableLoadingRows: {
      type: Boolean,
      default: false
    },
    innerTableQuery: {
      type: Object,
      default: () => ({
        totalRecords: 0,
        dataFetched: false,
        currentPage: 0,
        rowsPerPage: 5,
        rowLoading: false
      })
    },
    subFirst: {
      type: Number,
      default: 0
    },
    defaultSortField: {
      type: String,
      default: 'journey_created_date'
    },
    defaultSortOrder: {
      type: Number,
      default: -1
    },
    isMenuVisible: {
      type: Function,
      required: false,
      default: () => () => false,
    },
    tableHeader: {
      type: String,
    },
    retriggerPaymentEnable: {
      type: Function,
      required: false
    },
    showTrigger: {
      type: Boolean,
      default: false
    },
    loadingMessage: {
      type: String,
      default: "Loading Dashboard data. Please wait.",
    },
    getActions: {
      type: Function,
      default: () => () => [],
    },
    handleProposalJourney: {
      type: Function,
      required: false
    },
    journeyStatusLabelFormatter: {
      type: Function,
      required: false,
      default: null
    },
    journeyStatusSeverity: {
      type: Function,
      required: false,
      default: null
    },
    isJourneyStatusInteractive: {
      type: Function,
      required: false,
      default: null
    },
    showToggleAction: {
      type: Boolean,
      default: false
    },
    showDeleteAction: {
      type: Boolean,
      default: false
    },
    /** When true, root uses flex + viewport height and DataTable uses scrollable/flex so body scrolls inside the pane; horizontal scrollbar sits at the bottom of the scroll area. */
    fillViewportHeight: {
      type: Boolean,
      default: false
    },
    /** CSS height when fillViewportHeight is true (e.g. calc(100vh - 12rem) if a fixed header sits above). */
    viewportHeight: {
      type: String,
      default: '100vh'
    },
    /** When set, Status column (`value`) filter uses this dropdown; match mode comes from parent filters (contains). */
    statusColumnFilterOptions: {
      type: Array,
      default: null
    },
    /** When set, Insurance Company column (`insurance_company_code`) uses a single-select filter. */
    insuranceCompanyColumnFilterOptions: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: true,
      dataTable: null,
      sortField: this.defaultSortField,
      sortOrder: this.defaultSortOrder,
      filtersLocal: {},
      items: [],
      menu: null,
      expandedRows: {},
      subTableDataMap: {},
      loadingRows: {},
      subTableId: null,
      selectedRef: null,
      currentRole: { isAv: false }
    };
  },
  computed: {
    noData() {
      return "No data available";
    },
    fillViewportRootStyle() {
      if (!this.fillViewportHeight) {
        return null;
      }
      const style = {
        minHeight: 0,
        boxSizing: 'border-box',
        height: this.viewportHeight,
      };
      if (this.viewportHeight === '100%') {
        style.flex = '1 1 0';
      }
      return style;
    },
    /** Full-width table; with fillViewport, minWidth allows horizontal scroll when columns exceed width. */
    fluidTableStyle() {
      if (this.fillViewportHeight) {
        return { width: '100%', minWidth: 'max-content' };
      }
      return { width: '100%' };
    },
    /** Normalize `statusColumnFilterOptions` (strings or { Text, Value }) for MultiSelect. */
    statusFilterSelectOptions() {
      const o = this.statusColumnFilterOptions;
      if (!Array.isArray(o) || !o.length) return [];
      return o.map((item) => {
        if (item != null && typeof item === 'object') {
          const v = item.Value ?? item.value ?? item.Text ?? item.label;
          const t = item.Text ?? item.label ?? String(v ?? '');
          return { Text: t, Value: String(v ?? '') };
        }
        const s = String(item);
        return { Text: s, Value: s };
      });
    },
    insurerFilterSelectOptions() {
      if (!this.insuranceCompanyColumnFilterOptions || !Array.isArray(this.insuranceCompanyColumnFilterOptions)) {
        return [];
      }
      return this.insuranceCompanyColumnFilterOptions.map((row) => ({
        Text: row.insuranceCompanyName,
        Value: row.insuranceCompanyCode,
      }));
    },
  },
  watch: {
    subInnerTableDataMap(newVal) {
      this.subTableDataMap[this.subTableId] = newVal;
    },
    subTableLoadingRows(newVal) {
      this.loadingRows[this.subTableId] = newVal;
    },
    rowLoading(newVal) {
      this.loading = newVal;
    },
    filters(newVal) {
      this.filtersLocal = { ...newVal };
    }
  },
  methods: {
    stripHtml,
    /** Match health dashboard: any truthy `isFilter` enables the column filter. */
    columnHasFilter(col) {
      return !!(col && col.isFilter);
    },
    isColumnFilterActive(field) {
      if (!this.filtersLocal || !this.filtersLocal[field]) {
        return false;
      }
      const filter = this.filtersLocal[field];
      if (filter.constraints && Array.isArray(filter.constraints)) {
        return filter.constraints.some((c) => {
          const v = c.value;
          if (v == null || v === '') return false;
          if (typeof v === 'string' && v.trim() === '') return false;
          if (Array.isArray(v)) return v.length > 0;
          return true;
        });
      }
      if (filter.value != null && filter.value !== '') {
        if (typeof filter.value === 'string' && filter.value.trim() === '') return false;
        return true;
      }
      return false;
    },
    isStatusDropdownColumnFilter(col) {
      return col && col.field === 'value' && this.statusFilterSelectOptions.length > 0;
    },
    isInsurerDropdownColumnFilter(col) {
      return col && col.field === 'insurance_company_code' && this.insurerFilterSelectOptions.length > 0;
    },
    isColumnFilterApplyOnChange(col) {
      return this.isStatusDropdownColumnFilter(col) || this.isInsurerDropdownColumnFilter(col);
    },
    onFilterColumnClear(col, filterModel, filterCallback) {
      filterModel.value = null;
      if (col && col.field === 'insurance_company_code') {
        filterModel.matchMode = FilterMatchMode.EQUALS;
      } else {
        filterModel.matchMode = FilterMatchMode.CONTAINS;
      }
      filterCallback();
    },
    onInsurerFilterChange(val, filterModel, filterCallback) {
      const next =
        val != null && String(val).trim() !== '' ? String(val).trim() : null;
      filterModel.value = next;
      filterModel.matchMode = FilterMatchMode.EQUALS;
      filterCallback();
    },
    statusFilterModelValue(filterModel) {
      const v = filterModel?.value;
      if (v == null || v === '') return [];
      if (Array.isArray(v)) return v;
      return [v];
    },
    onStatusMultiFilterChange(val, filterModel, filterCallback) {
      const next = Array.isArray(val) ? val.filter((x) => x != null && String(x).trim() !== '') : [];
      filterModel.value = next.length ? next : null;
      filterModel.matchMode = FilterMatchMode.CONTAINS;
      filterCallback();
    },
    onFilter(event) {
      const filterConstraints = Object.keys(event.filters)
        .flatMap((key) =>
          (event.filters[key].constraints || []).flatMap((c) => {
            const mode = c.matchMode || FilterMatchMode.CONTAINS;
            const v = c.value;
            if (v == null || v === '') return [];
            if (Array.isArray(v)) {
              return v
                .map((item) => {
                  const raw =
                    item != null && typeof item === 'object' && 'Value' in item ? item.Value : item;
                  const s = raw == null ? '' : String(raw).trim();
                  return s ? `${key}.${mode}.${s}` : null;
                })
                .filter(Boolean);
            }
            const s = String(v).trim();
            return s ? [`${key}.${mode}.${s}`] : [];
          })
        );

      const filterFinal = filterConstraints.join(',');
      this.$emit('update-with-filter', filterFinal);
    },
    clearColumnFilter(field, filterModel, filterCallback) {
      filterModel.value = null;
      filterCallback();
    },
    showMenu(event, rowData) {
      const actions = typeof this.getActions === 'function' ? this.getActions(rowData) : [];
      this.items = actions || [];
      if (this.items.length > 0) {
        this.$refs.menu.toggle(event);
      }
    },
    isDownloadProposalEnabled(rowData) {
      const userData = rowData.data;
      return ['Proposal Error', 'Proposal Submitted'].includes(userData.value);
    },
    handleProposalDownload(rowData) {
      console.log("Downloading proposal for:", rowData.data);
      this.getProposalReviewData(rowData.data.journey_id);
    },
    handlePolicyDownload(rowData) {
      console.log("Downloading policy for:", rowData.data);
      this.getProposalReviewData(rowData.data.journey_id);
    },
    async getProposalReviewData(journeyId) {
      const payload = {
        journey_id: journeyId,
        stage: "Proposal Review"
      };
      console.log('response', payload);
    },
    async onRowExpand(event) {
      this.subTableId = event.data.id;
      this.selectedRef = event.data.payment_reference_no;
      this.expandedRows = { [this.subTableId]: true };

      this.loadingRows[this.subTableId] = true;
      this.$emit('fetch-payment-records', this.selectedRef);
    },
    onRowCollapse(event) {
      this.subTableId = event.data.id;
      delete this.expandedRows[this.subTableId];
      delete this.subTableDataMap[this.subTableId];
    },
    onSort(event) {
      this.sortField = event.sortField;
      this.sortOrder = event.sortOrder;

      this.tableData.sort((a, b) => {
        const value1 = a[this.sortField];
        const value2 = b[this.sortField];

        let result = 0;

        if (value1 == null && value2 != null) result = -1;
        else if (value1 != null && value2 == null) result = 1;
        else if (value1 == null && value2 == null) result = 0;
        else if (!isNaN(value1) && !isNaN(value2)) {
          const num1 = parseFloat(value1);
          const num2 = parseFloat(value2);
          result = num1 < num2 ? -1 : num1 > num2 ? 1 : 0;
        } else if (typeof value1 === 'string' && typeof value2 === 'string') {
          result = value1.localeCompare(value2);
        } else {
          result = 0;
        }

        return this.sortOrder === 1 ? result : -result;
      });
    },
    handleReset() {
      this.$emit('update-with-filter', '');
      this.$emit('update:selectedRecord', []);
    },
    onPage(event) {
      this.$emit('page-change', {
        page: event.page,
        rows: event.rows,
        first: event.first
      });
    },
    innerPageChange(event) {
      this.$emit('inner-page-change', {
        page: event.page,
        rows: event.rows,
        first: event.first,
        selectedRef: this.selectedRef,
      });
    },
    escapeCsvCell(value) {
      if (value === null) {
        return 'null';
      }
      if (value === undefined) {
        return '';
      }
      const s = String(value);
      if (/[",\n\r]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    },
    exportCSV() {
      const rows = Array.isArray(this.tableData) ? this.tableData : [];
      const cols = Array.isArray(this.columns)
        ? this.columns.filter((c) => c && c.field)
        : [];

      let csvContent = '\uFEFF';

      if (rows.length === 0) {
        console.warn('exportCSV: no rows to export.');
        return;
      }

      const colFields = new Set(cols.map((c) => c.field));
      const extraKeySet = new Set();
      rows.forEach((row) => {
        if (row && typeof row === 'object') {
          Object.keys(row).forEach((k) => {
            if (!colFields.has(k)) {
              extraKeySet.add(k);
            }
          });
        }
      });
      const extraKeys = [...extraKeySet].sort();

      const headerCells = [
        ...cols.map((c) => this.escapeCsvCell(c.header ?? c.field)),
        ...extraKeys.map((k) => this.escapeCsvCell(k)),
      ];
      csvContent += headerCells.join(',') + '\n';

      rows.forEach((row) => {
        const valueCells = [
          ...cols.map((c) => this.escapeCsvCell(row?.[c.field])),
          ...extraKeys.map((k) => this.escapeCsvCell(row?.[k])),
        ];
        csvContent += valueCells.join(',') + '\n';
      });

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${this.fileName || 'export'}.csv`);

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        window.open('data:text/csv;charset=utf-8,' + encodeURIComponent(csvContent));
      }
    },
    getInsFullName() {
      return {};
    },
    formatDate(date) {
      if (!date) {
        return '--'
      }
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
      const month = monthNames[d.getMonth()]
      const year = d.getFullYear()
      return `${day}-${month}-${year}`
    },
    handleClick(slotProps) {
      this.$emit('handle-proposal-journey', slotProps);
    },
    valueColumnLabel(row) {
      if (this.journeyStatusLabelFormatter) {
        return this.journeyStatusLabelFormatter(row);
      }
      return row?.value || '--';
    },
    valueColumnSeverity(row) {
      if (this.journeyStatusSeverity) {
        return this.journeyStatusSeverity(row);
      }
      return this.getStatusColor(row?.value);
    },
    isValueColumnInteractive(row) {
      if (this.isJourneyStatusInteractive) {
        return this.isJourneyStatusInteractive(row);
      }
      return this.handleProposalJourney && this.handleProposalJourney(row);
    },
    onValueColumnClick(row) {
      if (!this.isValueColumnInteractive(row)) {
        return;
      }
      this.$emit('handle-proposal-journey', row);
    },
    formatPremiumINR(val) {
      if (val === null || val === undefined || val === '') {
        return '--';
      }
      const n = Number(val);
      if (Number.isNaN(n)) {
        return '--';
      }
      return formatToINR(n);
    },
    async copyJourneyId(value) {
      const text = String(value ?? '').trim();
      if (!text) {
        return;
      }
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement('textarea');
          ta.value = text;
          ta.setAttribute('readonly', '');
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          document.execCommand('copy');
          document.body.removeChild(ta);
        }
        if (this.$toast) {
          this.$toast.add({
            severity: 'success',
            summary: 'Copied',
            detail: 'Journey Id copied to clipboard',
            life: 2000,
          });
        }
      } catch (e) {
        console.error('copyJourneyId', e);
        if (this.$toast) {
          this.$toast.add({
            severity: 'error',
            summary: 'Copy failed',
            detail: 'Could not copy Journey Id',
            life: 2500,
          });
        }
      }
    },
    rowSelectable(rowData) {
      return rowData.role !== 'Customer';
    },
    isRowActive(row) {
      return (
        row.active === 1 ||
        row.active === true ||
        row.isActive === 1 ||
        row.isActive === true
      );
    },
    handleToggleChange(rowData) {
      this.$emit('toggle-action', rowData);
    },
    handleDeleteClick(rowData) {
      this.$emit('delete-action', rowData);
    },
    getStatusColor(value) {
      if (value === 'Active' || value === 'active' || value === 1 || value === true) return 'success';
      if (value === 'Inactive' || value === 'inactive' || value === 0 || value === false) return 'secondary';
      return 'info';
    }
  },
  mounted() {
    this.dataTable = this.$refs.dataTable;
    this.menu = this.$refs.menu;
  }
};
</script>

<style scoped>
/* Dark theme: global `motor-dashboard-prime-dark.scss` (`.dark` + `body.dark`) */
.common-table-root {
  padding: 0 !important;
  padding-bottom: 2rem !important;
}

.common-table-root.common-table-root--fill-viewport {
  display: flex;
  flex-direction: column;
  padding-bottom: 0 !important;
}

.common-table-root--fill-viewport .common-table-datatable-shell {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.common-table-root--fill-viewport :deep(.custom-datatable.p-datatable) {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.common-table-root--fill-viewport :deep(.custom-datatable .p-datatable-table-container) {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.common-table-root :deep(.custom-datatable .p-datatable-table-container) {
  scrollbar-width: thin;
  scrollbar-color: #8b95a5 #e8eaed;
}

.common-table-root :deep(.custom-datatable .p-datatable-table-container::-webkit-scrollbar) {
  width: 10px;
  height: 10px;
}

.common-table-root :deep(.custom-datatable .p-datatable-table-container::-webkit-scrollbar-track) {
  background: #e8eaed;
  border-radius: 6px;
}

.common-table-root :deep(.custom-datatable .p-datatable-table-container::-webkit-scrollbar-thumb) {
  background: #8b95a5;
  border-radius: 6px;
  border: 2px solid #e8eaed;
}

.common-table-root :deep(.custom-datatable .p-datatable-table-container::-webkit-scrollbar-thumb:hover) {
  background: #5c6570;
}

.journey-id-cell {
  min-width: 0;
  max-width: 100%;
}

.journey-id-cell__text {
  min-width: 0;
}

/* Neutral copy control (avoid strong brand fill on row icon) */
:deep(.journey-id-copy-btn.p-button) {
  flex-shrink: 0;
  width: 2rem !important;
  height: 2rem !important;
  padding: 0 !important;
  color: #6c757d !important;
}

:deep(.journey-id-copy-btn.p-button:hover:not(:disabled)) {
  background: rgba(0, 0, 0, 0.06) !important;
  color: #1a1b1e !important;
}

/* Smaller column header labels (PrimeVue DataTable) */
/* Ensure global `.cursor-pointer` applies to Prime Tag root when class is forwarded */
.common-table-root :deep(.p-tag.cursor-pointer) {
  cursor: pointer !important;
}

.common-table-root :deep(.p-tag.cursor-not-allowed) {
  cursor: not-allowed !important;
}

.common-table-root :deep(.p-datatable.custom-datatable .p-datatable-table) {
  width: 100%;
  table-layout: auto;
}

.common-table-root :deep(.p-datatable.custom-datatable) {
  .p-datatable-header-cell {
    font-size: 0.85rem;
    line-height: 1.25;
  }

  .p-datatable-column-title {
    font-size: inherit;
    font-weight: 600;
  }

  .p-datatable-column-header-content {
    font-size: inherit;
    gap: 0.25rem;
  }
}
</style>

<style>
/* Unscoped: PrimeVue filter menu / multiselect panel may render outside the table subtree. */
.common-table-column-filter-menu {
  width: 350px !important;
  max-width: calc(100vw - 16px) !important;
  box-sizing: border-box;
}

.common-table-column-filter-menu .column-filter-inner {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.common-table-column-filter-menu .column-filter-multiselect,
.common-table-column-filter-menu .column-filter-multiselect.p-multiselect,
.common-table-column-filter-menu .column-filter-multiselect.p-select,
.common-table-column-filter-menu .column-filter-input,
.common-table-column-filter-menu .p-inputtext {
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;
}

.common-table-column-filter-menu .column-filter-actions {
  flex-wrap: wrap;
  gap: 0.35rem;
}

.p-multiselect-panel.common-table-status-multiselect-panel,
.common-table-status-multiselect-panel.p-multiselect-panel {
  width: min(150px, calc(100vw - 24px)) !important;
  max-width: calc(100vw - 24px) !important;
  min-width: 0 !important;
  box-sizing: border-box;
}
</style>
